import type { AxiosError } from 'axios';
import { useMemo } from 'react';
import useSWR from 'swr';
import { getAccount } from '~/api/account';
import type { GetAccountResponse } from '~/api/aspida/@types';
import { accountServices } from '~/features/account/accountService';
import type { ChildId } from '~/features/account/user';

// SWRキャッシュキー
export const ACCOUNT_KEY = 'account';

// GET /account のSWRフック
export const useGetAccount = () => {
  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSWR<GetAccountResponse, AxiosError>(ACCOUNT_KEY, () => getAccount({}), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  // adapter は呼び出すたびに新しいオブジェクトを返すため、
  // useMemo で参照を安定化させて useEffect の無限ループを防ぐ
  const user = useMemo(() => (data ? accountServices.userAdapter(data) : undefined), [data]);
  const children = useMemo(() => (data ? accountServices.childrenAdapter(data) : []), [data]);

  return { user, children, rawData: data, error, isLoading, revalidate };
};

// 派生フック（Recoilのgettersを置き換え）
export const useUser = () => {
  const { user, isLoading, error } = useGetAccount();
  return { data: user, isLoading, error };
};

export const useChildren = () => {
  const { children, isLoading, error } = useGetAccount();
  return { data: children, isLoading, error };
};

export const useSelectedChild = () => {
  const { user, children, isLoading, error } = useGetAccount();
  const selectedChild = useMemo(
    () => children.find((c) => c.child_id === user?.selected_child_id),
    [children, user?.selected_child_id],
  );
  return { data: selectedChild, isLoading, error };
};

// 指定IDの子情報を取得する派生フック
export const useChild = (childId: ChildId | undefined) => {
  const { children, isLoading, error } = useGetAccount();
  const data = useMemo(
    () => (childId != null ? children.find((c) => c.child_id === childId) : undefined),
    [children, childId],
  );
  return { data, isLoading, error };
};
